import { useQuery } from "@tanstack/react-query";
import axiosInstance from "service/axiosInstance";
import { API_URLS } from "service/url";
import { QUERY_KEYS } from "service/querykeys";

export interface ResolvedProject {
  projectUuid: string;
  status?: boolean;
  message?: string;
}

export interface ResolvedWorkItem {
  projectUuid: string;
  workItemUuid: string;
  status?: boolean;
  message?: string;
}

/**
 * Resolves a projectKey to its projectUuid.
 */
export const useResolveProjectKey = (orgSlug?: string, projectKey?: string) => {
  return useQuery({
    queryKey: QUERY_KEYS.RESOLVE_PROJECT(orgSlug || "", projectKey || ""),
    queryFn: async () => {
      if (!orgSlug || !projectKey || orgSlug === "org") return null;
      const { data } = await axiosInstance.get(
        API_URLS.RESOLVE_PROJECT(orgSlug, projectKey)
      );
      return data?.response as ResolvedProject;
    },
    enabled: !!orgSlug && !!projectKey && orgSlug !== "org",
    staleTime: 1000 * 60 * 60, // 1 hour - keys are stable
  });
};

/**
 * Resolves a projectKey + workItemKey to projectUuid and workItemUuid.
 */
export const useResolveWorkItemKey = (
  orgSlug?: string,
  projectKey?: string,
  workItemKey?: string
) => {
  return useQuery({
    queryKey: QUERY_KEYS.RESOLVE_WORK_ITEM(
      orgSlug || "",
      projectKey || "",
      workItemKey || ""
    ),
    queryFn: async () => {
      if (!orgSlug || !projectKey || !workItemKey || orgSlug === "org")
        return null;
      const { data } = await axiosInstance.get(
        API_URLS.RESOLVE_WORK_ITEM(orgSlug, projectKey, workItemKey)
      );
      return data?.response as ResolvedWorkItem;
    },
    enabled: !!orgSlug && !!projectKey && !!workItemKey && orgSlug !== "org",
    staleTime: 1000 * 60 * 60, // 1 hour
  });
};
